// Turns korm's column metadata (GET /api/meta/schema/:model) into form-field descriptors.
// Pure and separate from CrudPage so it can be tested without rendering anything.

// Never editable: the primary key identifies the row being edited, and the timestamps are
// maintained by the database's own defaults. Offering them invites a user to break a row's
// identity or backdate an audit trail.
const NEVER_EDITABLE = new Set(['created_at', 'updated_at', 'deleted_at']);

/** Column type → the input widget that can actually represent it. */
export function widgetFor(type) {
  const t = String(type || '').toLowerCase();
  if (t === 'boolean') return 'checkbox';
  if (t === 'integer' || t === 'bigint' || t === 'decimal' || t === 'float') return 'number';
  if (t === 'text' || t === 'longtext' || t === 'mediumtext') return 'textarea';
  if (t === 'date') return 'date';
  if (t === 'timestamp' || t === 'datetime') return 'datetime-local';
  return 'text';
}

/**
 * @param {Array} columns korm's describeModel().columns
 * @returns {Array<{name, type, widget, required}>} in declaration order
 */
export function fieldsFor(columns) {
  return (columns || [])
    .filter((c) => c && !c.primaryKey && !c.autoIncrement && !NEVER_EDITABLE.has(c.name))
    .map((c) => ({
      name: c.name,
      type: c.type,
      widget: widgetFor(c.type),
      // A column is only genuinely required when it can't be null AND the database has no
      // default to fall back on — otherwise the form demands a value the schema would supply.
      required: c.nullable === false && c.default == null,
    }));
}

/** Table columns: everything described, in order. Independent of whether any rows exist. */
export function columnsFor(columns) {
  return (columns || []).map((c) => ({ key: c.name, label: c.name }));
}

/** Seed a form's state — an empty value per field, or the row's values when editing. */
export function initialValues(fields, row) {
  const out = {};
  for (const f of fields) {
    const v = row ? row[f.name] : undefined;
    if (f.widget === 'checkbox') out[f.name] = Boolean(v);
    else out[f.name] = v == null ? '' : String(v);
  }
  return out;
}

/**
 * Form state → a korm `data` payload.
 * Blank optional fields become null rather than "" — an empty string in a nullable integer
 * column is a type error on Postgres and a silent 0 on MySQL.
 */
export function toPayload(fields, values) {
  const out = {};
  for (const f of fields) {
    const v = values[f.name];
    if (f.widget === 'checkbox') out[f.name] = Boolean(v);
    else if (v === '' || v === undefined) out[f.name] = null;
    else if (f.widget === 'number') out[f.name] = Number(v);
    else out[f.name] = v;
  }
  return out;
}
